import {
    GridComparatorFn,
    GridSortCellParams,
    GridSortDirection,
} from '@mui/x-data-grid';
import DataFormQuestion from '../../models/DataRequestHub/DataFormQuestion';

const getValue = (row: DataFormQuestion, field: string): unknown => {
    return field.split('.').reduce((obj: any, key) => obj?.[key], row);
};

const compareValues = (aVal: unknown, bVal: unknown): number => {
    const aEmpty = aVal === null || aVal === undefined || aVal === '';
    const bEmpty = bVal === null || bVal === undefined || bVal === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof aVal === 'number' && typeof bVal === 'number' && !isNaN(aVal) && !isNaN(bVal)) {
        return aVal - bVal;
    }
    const aTime =
        aVal instanceof Date
            ? aVal.getTime()
            : typeof aVal === 'string'
              ? Date.parse(aVal)
              : NaN;
    const bTime =
        bVal instanceof Date
            ? bVal.getTime()
            : typeof bVal === 'string'
              ? Date.parse(bVal)
              : NaN;
    if (!isNaN(aTime) && !isNaN(bTime)) {
        return aTime - bTime;
    }
    return String(aVal).localeCompare(String(bVal));
};

const resolveParent = (
    row: DataFormQuestion,
    rows: DataFormQuestion[]
): DataFormQuestion => {
    if (!row.isSubQuestion || !row.parentQuestionId) return row;
    const parent = rows.find((r) => r.customQuestionId === row.parentQuestionId);
    return parent ?? row;
};

export function getQuestionnaireGroupedSortComparator(
    field: string
): (sortDirection: GridSortDirection) => GridComparatorFn {
    return (sortDirection: GridSortDirection) => {
        const directionMultiplier = sortDirection === 'desc' ? -1 : 1;
        return (
            _v1: unknown,
            _v2: unknown,
            cellParams1: GridSortCellParams,
            cellParams2: GridSortCellParams
        ): number => {
            const api = cellParams1.api;
            const rowModels = api.getRowModels();
            const rows = Array.from(rowModels.values()) as DataFormQuestion[];
            const row1 = api.getRow(cellParams1.id) as DataFormQuestion | null;
            const row2 = api.getRow(cellParams2.id) as DataFormQuestion | null;
            if (!row1 || !row2) return 0;
            const parent1 = resolveParent(row1, rows);
            const parent2 = resolveParent(row2, rows);
            const parent1Val = getValue(parent1, field);
            const parent2Val = getValue(parent2, field);
            const parentCompare = compareValues(parent1Val, parent2Val);
            if (parentCompare !== 0) {
                return parentCompare * directionMultiplier;
            }
            const sameParent =
                parent1.customQuestionId === parent2.customQuestionId;
            if (!sameParent) return 0;
            if (!row1.isSubQuestion && row2.isSubQuestion) return -1;
            if (row1.isSubQuestion && !row2.isSubQuestion) return 1;
            if (row1.isSubQuestion && row2.isSubQuestion) {
                return (row1.displayOrderSub ?? 0) - (row2.displayOrderSub ?? 0);
            }
            return 0;
        };
    };
}
